import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { auth } from "../api/client";
import { ErrorBox, Field, Wordmark, errorMessage } from "../components/ui";
import { t } from "../i18n";
import { isValidEmail } from "../lib/validators";

/** Quatro telas públicas de recuperação de acesso: pedir link (senha / 2FA) e concluir com o token do e-mail. */

function Shell({ title, lead, children }: { title: string; lead: string; children: React.ReactNode }) {
  return (
    <div className="auth-wrap">
      <div className="card auth-card">
        <Wordmark style={{ fontSize: 22 }} />
        <div className="stack s8">
          <h1 className="lead" style={{ fontSize: 22 }}>{title}</h1>
          <p className="ink2">{lead}</p>
        </div>
        {children}
        <p className="ink2" style={{ fontSize: 13 }}>
          <Link to="/entrar">{t.auth.backToSignIn}</Link>
        </p>
      </div>
    </div>
  );
}

function RequestLink({ kind }: { kind: "password" | "totp" }) {
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isPassword = kind === "password";

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!isValidEmail(email)) return setError(t.auth.invalidEmail);
    setPending(true);
    try {
      await (isPassword ? auth.forgotPassword(email.trim()) : auth.forgotTotp(email.trim()));
      setSent(true);
    } catch (x) {
      setError(errorMessage(x));
    } finally {
      setPending(false);
    }
  }

  return (
    <Shell title={isPassword ? t.auth.forgotTitle : t.auth.lostTotpTitle} lead={isPassword ? t.auth.forgotLead : t.auth.lostTotpLead}>
      {sent ? (
        <div className="banner ok" role="status">{t.auth.forgotSent}</div>
      ) : (
        <form onSubmit={submit} className="stack" noValidate>
          <Field label={t.auth.email}>
            {(id) => <div className="inp"><input id={id} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>}
          </Field>
          {error && <ErrorBox error={new Error(error)} />}
          <button className="btn full" type="submit" disabled={pending}>{pending ? t.common.loading : t.auth.forgotSend}</button>
        </form>
      )}
    </Shell>
  );
}

export function EsqueciSenha() { return <RequestLink kind="password" />; }
export function Recuperar2fa() { return <RequestLink kind="totp" />; }

export function RedefinirSenha() {
  const [sp] = useSearchParams();
  const token = sp.get("token") ?? "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 10) return setError(t.auth.weakPassword);
    if (password !== confirm) return setError(t.auth.passwordsDiffer);
    setPending(true);
    try {
      await auth.resetPassword(token, password);
      setDone(true);
      setPassword("");
      setConfirm("");
      window.setTimeout(() => navigate("/entrar", { replace: true }), 2500);
    } catch (x) {
      setError(errorMessage(x));
    } finally {
      setPending(false);
    }
  }

  return (
    <Shell title={t.auth.resetTitle} lead={t.auth.resetLead}>
      {!token ? (
        <ErrorBox error={new Error(t.auth.tokenMissing)} />
      ) : done ? (
        <div className="banner ok" role="status">{t.auth.resetDone}</div>
      ) : (
        <form onSubmit={submit} className="stack" noValidate>
          <Field label={t.auth.newPassword} hint={t.auth.passwordHint}>
            {(id, d) => <div className="inp"><input id={id} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-describedby={d} required /></div>}
          </Field>
          <Field label={t.auth.confirmPassword}>
            {(id) => <div className="inp"><input id={id} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required /></div>}
          </Field>
          {error && <ErrorBox error={new Error(error)} />}
          <button className="btn full" type="submit" disabled={pending}>{pending ? t.common.loading : t.auth.resetSubmit}</button>
        </form>
      )}
    </Shell>
  );
}

export function Redefinir2fa() {
  const [sp] = useSearchParams();
  const token = sp.get("token") ?? "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!password) return setError(t.auth.fillAll);
    setPending(true);
    try {
      await auth.resetTotp(token, password);
      setDone(true);
      setPassword("");
      window.setTimeout(() => navigate("/entrar", { replace: true }), 3000);
    } catch (x) {
      setError(errorMessage(x));
    } finally {
      setPending(false);
    }
  }

  return (
    <Shell title={t.auth.resetTotpTitle} lead={t.auth.resetTotpLead}>
      {!token ? (
        <ErrorBox error={new Error(t.auth.tokenMissing)} />
      ) : done ? (
        <div className="banner ok" role="status">{t.auth.resetTotpDone}</div>
      ) : (
        <form onSubmit={submit} className="stack" noValidate>
          <Field label={t.auth.password}>
            {(id) => <div className="inp"><input id={id} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>}
          </Field>
          {error && <ErrorBox error={new Error(error)} />}
          <button className="btn full" type="submit" disabled={pending}>{pending ? t.common.loading : t.auth.resetTotpSubmit}</button>
        </form>
      )}
    </Shell>
  );
}
